import { BenefitDocumentDto } from '@zorro/clients';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { Dropzone } from '@zorro/design-system/v2/Dropzone';
import { Box, FormErrorMessage, Stack, Text } from '@zorro/web/design-system';
import {
  EnrollmentConfirmationFormFields,
  UploadedFileBox,
  useAnalytics,
} from '@zorro/web/shared-ui';
import {
  downloadBenefitDocument,
  showErrorNotification,
} from '@zorro/web/ui-utils';
import { Controller, UseFormReturn } from 'react-hook-form';

type Props = {
  sectionNumber: number;
  onboardingPeriodId: string;
  enrollmentConfirmationForm: UseFormReturn<EnrollmentConfirmationFormFields>;
};

export const EnrollmentEvidenceSection = ({
  sectionNumber,
  onboardingPeriodId,
  enrollmentConfirmationForm,
}: Props) => {
  const { sendEvent } = useAnalytics();
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = enrollmentConfirmationForm;

  const confirmationEvidence = watch('confirmationEvidence');
  const noEvidenceAvailable = watch('noEvidenceAvailable');

  const handleDrop = async (files: File[]) => {
    const existing = Array.isArray(confirmationEvidence)
      ? confirmationEvidence
      : [];
    setValue('confirmationEvidence', [...existing, ...files], {
      shouldValidate: true,
      shouldDirty: true,
    });
    await sendEvent('upload_confirmation_evidence', {
      onboarding_period_id: onboardingPeriodId,
      number_of_files: files.length,
    });
  };

  const handleDelete = (evidence: File | BenefitDocumentDto) => {
    const existing = Array.isArray(confirmationEvidence)
      ? confirmationEvidence
      : [];
    setValue(
      'confirmationEvidence',
      existing.filter((item) => item !== evidence),
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );
  };

  const handleDownload = async (document: BenefitDocumentDto) =>
    downloadBenefitDocument(document.documentUrl, document.fileName);

  return (
    <Stack my="xl" ml="xs" gap="xxs">
      <Text w="100%" size="md" fw={600}>
        {sectionNumber}. Upload submission evidence
      </Text>
      <Stack ml="md" gap="sm">
        <Text size="sm" c="var(--color-text-default-secondary)">
          Upload screenshots, email confirmations, or any other confirmation
          evidence
        </Text>
        <Dropzone
          multiple
          disabled={noEvidenceAvailable}
          onDrop={handleDrop}
          onReject={(files) => {
            const firstError = files[0]?.errors[0];
            if (firstError) {
              showErrorNotification({
                message: firstError.message,
                title: firstError.code,
              });
            }
          }}
        >
          <Dropzone.Empty title="Upload evidence" subtitle="Or drag it here" />
        </Dropzone>
        {Array.isArray(confirmationEvidence) &&
          confirmationEvidence.map((evidence) => (
            <Box
              pb="md"
              key={evidence instanceof File ? evidence.name : evidence.fileName}
            >
              <UploadedFileBox
                variant="secondary"
                onClickDelete={() => handleDelete(evidence)}
                name={
                  evidence instanceof File ? evidence.name : evidence.fileName
                }
                onClickDownload={
                  evidence instanceof File
                    ? undefined
                    : () => handleDownload(evidence)
                }
              />
            </Box>
          ))}
        {!noEvidenceAvailable && (
          <FormErrorMessage fieldName="confirmationEvidence" errors={errors} />
        )}
        <Controller
          control={control}
          name="noEvidenceAvailable"
          render={({ field: { value, ...rest } }) => (
            <Checkbox
              {...rest}
              checked={Boolean(value)}
              label="No evidence available"
              disabled={Boolean(
                Array.isArray(confirmationEvidence) &&
                confirmationEvidence.length > 0
              )}
            />
          )}
        />
      </Stack>
    </Stack>
  );
};
